import {
  communityReportCopy,
  countCommunityReportCopy,
  formatCommunityReportCopy,
} from "./community-report-copy.js";
import {
  initializeSiteSettingsMenu,
  normalizeSiteLanguage,
  normalizeSiteTheme,
  storedSiteLanguage,
  storedSiteTheme,
} from "./site-settings.js";
import {
  closestCountShadingHandle,
  countForPointerPosition,
  countShadingMaximum,
  DEFAULT_COUNT_SHADING_THRESHOLDS,
  nextCountShadingThresholds,
  normalizeCountShadingThresholds,
} from "./src/count-shading.js";
import {
  fetchCountyGeometry,
  joinCountyCountsToGeometry,
} from "./src/county-map/data.js";
import {
  formatCountyNumber,
  initializeCountyMap,
} from "./src/county-map/map.js";

export function initializeCountyApp(
  documentRoot = document,
  storage = localStorage,
  {
    fetchImpl = fetch,
    mapInitializer = initializeCountyMap,
  } = {},
) {
  const mapElement = requiredElement(documentRoot, "countyMap");
  const statusElement = requiredElement(documentRoot, "countyStatus");
  const shadingControl = documentRoot.querySelector("[data-county-shading-control]");
  if (!shadingControl) {
    throw new Error("County map controls are missing.");
  }

  const view = documentRoot.defaultView || window;
  const state = {
    language: storedSiteLanguage(storage),
    theme: storedSiteTheme(storage),
    status: "loading",
    joined: null,
    maximum: 25,
    thresholds: { ...DEFAULT_COUNT_SHADING_THRESHOLDS },
    mapController: null,
    mapGeneration: 0,
    mapInitialization: Promise.resolve(),
    mapUnavailable: false,
    geometryAbortController: null,
    destroyed: false,
  };

  const copy = (key) => communityReportCopy(state.language, key);
  const formatCopy = (key, replacements) => formatCommunityReportCopy(
    state.language,
    key,
    replacements,
  );
  const countCopy = (oneKey, manyKey, count) => countCommunityReportCopy(
    state.language,
    oneKey,
    manyKey,
    count,
    { count: formatCountyNumber(count, state.language) },
  );

  function renderPageCopy() {
    documentRoot.title = copy("county_document_title");
    documentRoot.querySelectorAll("[data-county-copy]").forEach((element) => {
      element.textContent = copy(element.dataset.countyCopy);
    });
    documentRoot.querySelectorAll("[data-county-copy-aria]").forEach((element) => {
      element.setAttribute("aria-label", copy(element.dataset.countyCopyAria));
    });
    mapElement.setAttribute("aria-label", copy("county_map_aria"));
    renderShadingControl();
    renderLegend();
    renderStatus();
    if (state.mapUnavailable) renderMapUnavailable();
    state.mapController?.setLanguage(state.language);
  }

  function renderShadingControl() {
    const { lowerCount, upperCount } = state.thresholds;
    shadingControl.innerHTML = `
      <section class="hex-poc-quantity-controls county-shading-control" aria-label="${escapeAttribute(copy("county_thresholds_aria"))}">
        <div class="hex-poc-quantity-heading">
          <p>${escapeText(copy("county_thresholds"))}</p>
          <span>${escapeText(copy("county_threshold_instruction"))}</span>
        </div>
        <div class="hex-poc-quantity-cutoff-labels">
          <span>${formatCopy("county_yellow_orange", { count: `<output for="countyLowerThreshold" data-county-lower-threshold>${escapeText(formatSickReportCount(lowerCount))}</output>` })}</span>
          <span>${formatCopy("county_orange_red", { count: `<output for="countyUpperThreshold" data-county-upper-threshold>${escapeText(formatSickReportCount(upperCount))}</output>` })}</span>
        </div>
        <div class="hex-poc-quantity-range-control county-shading-range-control" style="--lower-stop: ${thresholdPosition(lowerCount)}%; --upper-stop: ${thresholdPosition(upperCount)}%">
          <input id="countyLowerThreshold" class="hex-poc-quantity-lower-handle" type="range" min="0" max="${state.maximum}" step="1" value="${lowerCount}" aria-label="${escapeAttribute(copy("county_yellow_orange_aria"))}" aria-valuetext="${escapeAttribute(formatSickReportCount(lowerCount))}" data-county-threshold="lowerCount">
          <input id="countyUpperThreshold" class="hex-poc-quantity-upper-handle" type="range" min="0" max="${state.maximum}" step="1" value="${upperCount}" aria-label="${escapeAttribute(copy("county_orange_red_aria"))}" aria-valuetext="${escapeAttribute(formatSickReportCount(upperCount))}" data-county-threshold="upperCount">
        </div>
      </section>
    `;
  }

  function renderLegend() {
    const { lowerCount, upperCount } = state.thresholds;
    const low = documentRoot.querySelector('[data-county-legend="low"]');
    const middle = documentRoot.querySelector('[data-county-legend="middle"]');
    const high = documentRoot.querySelector('[data-county-legend="high"]');
    if (low) {
      low.textContent = formatCopy("county_legend_low", {
        count: formatSickReportCount(lowerCount),
      });
    }
    if (middle) {
      middle.textContent = formatCopy("county_legend_middle", {
        lower: formatCountyNumber(lowerCount, state.language),
        upper: formatSickReportCount(upperCount),
      });
    }
    if (high) {
      high.textContent = formatCopy("county_legend_high", {
        count: formatSickReportCount(upperCount),
      });
    }
  }

  function renderStatus() {
    const statusKeys = {
      loading: "county_loading",
      loaded: "county_loaded",
      geometryError: "county_geometry_unavailable",
      mapError: "county_map_unavailable",
    };
    const visibleStatus = state.status === "geometryError"
      ? state.status
      : state.mapUnavailable
        ? "mapError"
        : state.status;
    statusElement.textContent = copy(statusKeys[visibleStatus] || "county_loading");
    statusElement.classList.toggle("is-visually-hidden", visibleStatus === "loaded");
  }

  function renderMapLoading() {
    mapElement.replaceChildren(fallbackElement(documentRoot, copy("county_loading")));
  }

  function renderMapUnavailable() {
    mapElement.replaceChildren(fallbackElement(
      documentRoot,
      copy("county_map_unavailable"),
      copy("county_mapbox_help"),
    ));
  }

  function formatSickReportCount(count) {
    return countCopy("county_sick_report_one", "county_sick_report_many", count);
  }

  function thresholdPosition(count) {
    return (count / state.maximum) * 100;
  }

  function setThreshold(name, value) {
    const next = nextCountShadingThresholds(
      name,
      value,
      state.thresholds,
      state.maximum,
    );
    const changed = next.lowerCount !== state.thresholds.lowerCount
      || next.upperCount !== state.thresholds.upperCount;
    state.thresholds = { ...next };
    renderShadingControl();
    renderLegend();
    if (changed) state.mapController?.setThresholds(state.thresholds);
    return changed;
  }

  shadingControl.addEventListener("input", (event) => {
    const target = event.target;
    if (!target.matches("[data-county-threshold]")) return;
    setThreshold(target.dataset.countyThreshold, target.value);
  });

  shadingControl.addEventListener("pointerdown", (event) => {
    const rangeControl = event.target.closest(".county-shading-range-control");
    if (!rangeControl) return;
    event.preventDefault();
    const handle = closestCountShadingHandle(
      countForPointerPosition(
        event.clientX,
        rangeControl.getBoundingClientRect(),
        state.maximum,
      ),
      state.thresholds,
    );
    const updateHandle = (pointerEvent) => {
      const activeRange = shadingControl.querySelector(".county-shading-range-control");
      if (!activeRange) return;
      setThreshold(handle, countForPointerPosition(
        pointerEvent.clientX,
        activeRange.getBoundingClientRect(),
        state.maximum,
      ));
    };
    const finish = () => {
      view.removeEventListener("pointermove", updateHandle);
      view.removeEventListener("pointerup", finish);
      view.removeEventListener("pointercancel", finish);
    };
    view.addEventListener("pointermove", updateHandle);
    view.addEventListener("pointerup", finish);
    view.addEventListener("pointercancel", finish);
    updateHandle(event);
  });

  function recreateMap() {
    if (!state.joined || state.destroyed) return Promise.resolve();
    const generation = ++state.mapGeneration;
    const previousInitialization = state.mapInitialization;
    const initialization = (async () => {
      await previousInitialization;
      if (state.destroyed || generation !== state.mapGeneration) return;
      state.mapController?.destroy();
      state.mapController = null;
      state.mapUnavailable = false;
      renderStatus();
      renderMapLoading();
      const featureCollection = state.joined.featureCollection;
      try {
        const controller = await mapInitializer({
          element: mapElement,
          featureCollection,
          thresholds: state.thresholds,
          theme: state.theme,
          language: state.language,
        });
        if (state.destroyed || generation !== state.mapGeneration) {
          controller?.destroy();
          return;
        }
        state.mapController = controller;
        state.mapController?.setLanguage(state.language);
        renderStatus();
      } catch {
        if (state.destroyed || generation !== state.mapGeneration) return;
        state.mapUnavailable = true;
        renderStatus();
        renderMapUnavailable();
      }
    })();
    state.mapInitialization = initialization;
    return initialization;
  }

  function updateScaleFromJoinedData() {
    const counts = state.joined?.featureCollection.features
      .filter((feature) => feature.properties.hasCountyData)
      .map((feature) => feature.properties.sickCount) || [];
    state.maximum = countShadingMaximum(counts);
    state.thresholds = {
      ...normalizeCountShadingThresholds(state.thresholds, state.maximum),
    };
    renderShadingControl();
    renderLegend();
  }

  async function loadInitialData() {
    state.geometryAbortController?.abort();
    const controller = new globalThis.AbortController();
    state.geometryAbortController = controller;
    state.status = "loading";
    renderStatus();
    try {
      const geometry = await fetchCountyGeometry({ fetchImpl, signal: controller.signal });
      if (state.destroyed || controller.signal.aborted) return;
      state.joined = joinCountyCountsToGeometry(geometry);
      state.status = "loaded";
      updateScaleFromJoinedData();
      renderStatus();
      await recreateMap();
    } catch (error) {
      if (state.destroyed || controller.signal.aborted || error?.name === "AbortError") return;
      state.status = "geometryError";
      renderStatus();
      mapElement.replaceChildren(fallbackElement(
        documentRoot,
        copy("county_geometry_unavailable"),
      ));
    }
  }

  initializeSiteSettingsMenu({
    documentRoot,
    storage,
    copy: communityReportCopy,
    getLanguage: () => state.language,
    getTheme: () => state.theme,
    onLanguageChange: (language) => {
      state.language = normalizeSiteLanguage(language);
      renderPageCopy();
    },
    onThemeChange: (theme) => {
      const nextTheme = normalizeSiteTheme(theme);
      if (nextTheme === state.theme) return;
      state.theme = nextTheme;
      recreateMap();
    },
  });

  renderPageCopy();
  loadInitialData();

  return Object.freeze({
    destroy() {
      if (state.destroyed) return;
      state.destroyed = true;
      state.mapGeneration += 1;
      state.geometryAbortController?.abort();
      state.mapController?.destroy();
      state.mapController = null;
    },
    get state() {
      return Object.freeze({
        language: state.language,
        theme: state.theme,
        status: state.status,
        mapUnavailable: state.mapUnavailable,
        maximum: state.maximum,
        thresholds: Object.freeze({ ...state.thresholds }),
      });
    },
  });
}

function requiredElement(documentRoot, id) {
  const element = documentRoot.getElementById(id);
  if (!element) throw new Error(`Missing county map element: ${id}`);
  return element;
}

function fallbackElement(documentRoot, title, message = "") {
  const fallback = documentRoot.createElement("div");
  fallback.className = "map-fallback";
  const strong = documentRoot.createElement("strong");
  strong.textContent = title;
  fallback.append(strong);
  if (message) {
    const span = documentRoot.createElement("span");
    span.textContent = message;
    fallback.append(span);
  }
  return fallback;
}

function escapeText(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function escapeAttribute(value) {
  return escapeText(value)
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

if (typeof document !== "undefined" && typeof localStorage !== "undefined") {
  initializeCountyApp();
}
